import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, RotateCcw, Timer as TimerIcon, Waves, CloudRain, Wind, Volume2, VolumeX } from "lucide-react";
import { getAudioContext, playBell, playTripleBell, Ambience } from "../lib/audioEngine";

const PRESETS = [1, 3, 5, 10, 15, 20];

const NATURE = [
  { id: "off", label: "Silent", icon: VolumeX },
  { id: "ocean", label: "Ocean", icon: Waves },
  { id: "rain", label: "Rain", icon: CloudRain },
  { id: "wind", label: "Wind", icon: Wind },
];

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function MeditationTimer() {
  const [minutes, setMinutes] = useState(5);
  const [remaining, setRemaining] = useState(5 * 60);
  const [running, setRunning] = useState(false);
  const [nature, setNature] = useState("off");
  const intervalRef = useRef(null);
  const ambienceRef = useRef(null);

  // keep remaining in sync when idle and minutes change
  useEffect(() => {
    if (!running) setRemaining(minutes * 60);
  }, [minutes, running]);

  const stopAmbience = useCallback(() => {
    if (ambienceRef.current) {
      ambienceRef.current.stop();
      ambienceRef.current = null;
    }
  }, []);

  const startAmbience = useCallback(
    (type) => {
      stopAmbience();
      if (type === "off") return;
      const ctx = getAudioContext();
      if (!ctx) return;
      const amb = new Ambience(ctx, type);
      amb.start(0.28);
      ambienceRef.current = amb;
    },
    [stopAmbience]
  );

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      stopAmbience();
    };
  }, [stopAmbience]);

  const finish = useCallback(() => {
    setRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    const ctx = getAudioContext();
    playTripleBell(ctx, { freq: 396, duration: 3.5, volume: 0.5 });
    setTimeout(() => stopAmbience(), 4500);
    setRemaining(minutes * 60);
  }, [minutes, stopAmbience]);

  const start = () => {
    const ctx = getAudioContext();
    playBell(ctx, { freq: 528, duration: 3, volume: 0.5 }); // gentle start chime
    if (nature !== "off") startAmbience(nature);
    setRunning(true);
    intervalRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          finish();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
  };

  const pause = () => {
    setRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    stopAmbience();
  };

  const reset = () => {
    setRunning(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    stopAmbience();
    setRemaining(minutes * 60);
  };

  const chooseNature = (id) => {
    setNature(id);
    if (running) startAmbience(id);
  };

  const progress = 1 - remaining / (minutes * 60);

  return (
    <div className="rounded-2xl border border-neutral-100 bg-gradient-to-br from-[#eef7f6] to-white p-6 md:p-7 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-11 h-11 rounded-full bg-[var(--teal)] text-white flex items-center justify-center flex-shrink-0">
          <TimerIcon size={20} />
        </span>
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--teal-dark)] font-semibold">
            Practice Timer
          </p>
          <p className="font-serif-brand text-xl text-purple leading-tight">
            A silent sit, with a gentle chime
          </p>
        </div>
      </div>

      {/* Duration presets */}
      <div className="flex flex-wrap gap-2 mb-6">
        {PRESETS.map((m) => (
          <button
            key={m}
            onClick={() => !running && setMinutes(m)}
            disabled={running}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide-sm transition-all border ${
              minutes === m
                ? "bg-[var(--teal)] text-white border-[var(--teal)]"
                : "bg-white text-neutral-600 border-neutral-200 hover:border-[var(--teal)]"
            } ${running ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {m} min
          </button>
        ))}
      </div>

      {/* Circular countdown */}
      <div className="flex flex-col items-center">
        <div className="relative w-44 h-44">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#e5eeed" strokeWidth="6" />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="var(--teal)"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 45}`}
              strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress)}`}
              style={{ transition: "stroke-dashoffset 1s linear" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-serif-brand text-4xl text-purple tabular-nums">
              {fmt(remaining)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={running ? pause : start}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--teal)] text-white text-sm font-semibold tracking-wide-sm hover:bg-[var(--teal-dark)] transition-colors"
          >
            {running ? <Pause size={17} /> : <Play size={17} />}
            {running ? "Pause" : remaining < minutes * 60 ? "Resume" : "Begin"}
          </button>
          <button
            onClick={reset}
            aria-label="Reset"
            title="Reset"
            className="w-11 h-11 rounded-full border border-neutral-200 flex items-center justify-center text-neutral-600 hover:text-[var(--teal-dark)] hover:border-[var(--teal)] transition-colors"
          >
            <RotateCcw size={17} />
          </button>
        </div>
      </div>

      {/* Nature ambience */}
      <div className="mt-6 pt-5 border-t border-neutral-100">
        <p className="flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-neutral-500 mb-3">
          <Volume2 size={14} /> Background sound
        </p>
        <div className="flex flex-wrap gap-2">
          {NATURE.map((n) => (
            <button
              key={n.id}
              onClick={() => chooseNature(n.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide-sm transition-all border ${
                nature === n.id
                  ? "bg-[var(--teal-dark)] text-white border-[var(--teal-dark)]"
                  : "bg-white text-neutral-600 border-neutral-200 hover:border-[var(--teal)]"
              }`}
            >
              <n.icon size={14} /> {n.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
